import { reactive, readonly, markRaw } from 'vue'

let _id = 0
function nextId() {
  _id += 1
  return `modal_${_id}`
}

export function createModalController() {
  const state = reactive({
    stack: []
  })

  function open(entry) {
    const id = entry?.id || nextId()

    let closeOnEsc = entry?.options?.closeOnEsc !== false;
    let closeOnBackdrop = entry?.options?.closeOnBackdrop !== false;

    if (closeOnBackdrop && !closeOnEsc) {
      // If closeOnEsc is false, also force closeOnBackdrop to false
      closeOnBackdrop = false;
    }

    state.stack.push({
      id,
      component: entry.component ? markRaw(entry.component) : null,
      props: entry.props || {},
      options: {
        closeOnEsc: closeOnEsc,
        closeOnBackdrop: closeOnBackdrop,
      },
      resolve: typeof entry.resolve === 'function' ? entry.resolve : null,
      reject: typeof entry.reject === 'function' ? entry.reject : null,
    })

    return id
  }

  function close(id) {
    if (!state.stack.length) {
      return
    }

    if (!id) {
      state.stack.pop()
      return
    }

    const idx = state.stack.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    state.stack.splice(idx, 1)
  }

  function closeTop() {
    close()
  }

  function top() {
    if (!state.stack.length) {
      return null
    }
    return state.stack[state.stack.length - 1]
  }

  function confirm(options = {}) {
    return new Promise((resolve) => {
      open({
        props: {
          title: options.title || '',
          message: options.message || '',
          confirmText: options.confirmText || __('Okay'),
          cancelText: options.cancelText || null,
          modalWidth: options.modalWidth || '',
          danger: !!options.danger,
        },
        options: {
          closeOnEsc: options.closeOnEsc !== false,
          closeOnBackdrop: options.closeOnBackdrop !== false,
        },
        resolve,
      })
    })
  }

  function alert(options = {}) {
    // Check if a string was passed as options
    if (typeof options === 'string') {
      options = {
        message: options,
      };
    }

    return confirm({
      ...options,
      cancelText: null,
    })
  }

  const api = {
    state: readonly(state),
    open,
    close,
    closeTop,
    top,
    confirm,
    alert,
  }

  return api
}

const controller = createModalController() // create once, reuse

export function useModal() {
  return controller
}
